import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { DesignSystemConfig } from 'designSystem/content/types';
import { ICON_PACKS, DEFAULT_ICON_PACK } from 'dragonmouse-shared/constants';
import { buildGoogleFontsCdnUrl, buildCustomFontsCss } from 'dragonmouse-shared/ds';
import { VITE_FORGE_URL } from '@constants';
import { useTheme } from 'styled-components';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsMode, selectModeResolution } from 'redux/mode/reducers';
import * as S from './styles';

interface GalleryPreviewProps {
	storyHtml: string;
	isValidHtml?: boolean;
	rawCode: {
		theme: string;
		styles: string;
		componentsCss: string;
		componentsJs: string;
	};
	config?: DesignSystemConfig;
	assets?: Array<{ path: string; casHash: string }>;
	autoHeight?: boolean;
}

const GalleryPreview = ({
	storyHtml,
	isValidHtml = true,
	rawCode,
	config,
	assets,
	autoHeight = false,
}: GalleryPreviewProps) => {
	const { activeMode, previewBg, isDragging } = useSelector(
		(r: RootReducer) => ({
			activeMode: selectActiveDsMode(r.mode),
			previewBg: selectModeResolution(r.mode).previewBg,
			isDragging: r.mode.isDragging,
		}),
		shallowEqual
	);
	const styledTheme = useTheme();
	const iframeRef = React.useRef<HTMLIFrameElement>(null);

	// Resize iframe to fit content after load (only in autoHeight mode)
	const onIframeLoad = () => {
		const iframe = iframeRef.current;
		if (!iframe?.contentDocument?.body) return;

		if (autoHeight) {
			// Resize iframe to fit content
			iframe.style.height = '0px';
			const height = iframe.contentDocument.documentElement.scrollHeight;
			iframe.style.height = `${height}px`;
		}
	};

	// If gallery entry doesn't contain valid HTML, show error message instead of iframe
	if (isValidHtml === false) {
		return (
			<S.BrokenGalleryContainer>
				<S.BrokenGalleryIcon>⚠️</S.BrokenGalleryIcon>
				<S.BrokenGalleryTitle>Broken Gallery Entry</S.BrokenGalleryTitle>
				<S.BrokenGalleryMessage>
					This gallery entry contains invalid HTML code and cannot be rendered. The entry code may
					have been corrupted or contains plain text instead of HTML.
				</S.BrokenGalleryMessage>
				<S.BrokenGalleryCode>
					<pre>{storyHtml}</pre>
				</S.BrokenGalleryCode>
			</S.BrokenGalleryContainer>
		);
	}

	// Get icon pack configuration (use default if not specified or if pack is invalid/removed)
	const iconPack = config?.iconPack;
	const packId = iconPack && ICON_PACKS[iconPack] ? iconPack : DEFAULT_ICON_PACK;
	const packInfo = ICON_PACKS[packId];

	// Generate icon pack CDN include based on type (CSS link or JS script)
	const iconPackInclude =
		packInfo.type === 'css'
			? `<link rel="stylesheet" href="${packInfo.cdnLink}">`
			: `<script src="${packInfo.cdnLink}"></script>`;

	// Generate icon pack init script if needed (e.g., Lucide requires lucide.createIcons())
	const iconPackInit = packInfo.initScript ? `<script>${packInfo.initScript}</script>` : '';

	// Generate Google Fonts CDN link if fonts are configured
	const fonts = config?.fonts;
	const fontsCdnUrl = fonts && fonts.length > 0 ? buildGoogleFontsCdnUrl(fonts) : null;
	const fontsInclude = fontsCdnUrl
		? `<link rel="preconnect" href="https://fonts.googleapis.com">
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
	<link rel="stylesheet" href="${fontsCdnUrl}">`
		: '';

	// Generate @font-face rules for custom fonts
	const customFontsCss =
		fonts && fonts.length > 0 ? buildCustomFontsCss(fonts, `${VITE_FORGE_URL}/attachment`) : null;

	// Replace asset paths with signed URLs
	let processedHtml = storyHtml;
	let processedComponentsJs = rawCode.componentsJs || '';
	let processedComponentsCss = rawCode.componentsCss || '';
	let processedTheme = rawCode.theme || '';
	let processedStyles = rawCode.styles || '';

	if (assets && assets.length > 0) {
		// Create regex to match asset paths in src, href, and url() attributes
		assets.forEach((asset) => {
			const escapedPath = asset.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			// Match with or without leading slash, in various contexts
			const regex = new RegExp(`(src=["']|href=["']|url\\(['"]?)\\/?${escapedPath}(["']|\\))`, 'g');
			const signedUrl = `${VITE_FORGE_URL}/attachment/${encodeURIComponent(asset.casHash)}`;

			// Replace in HTML, JS, CSS, theme, and styles
			processedHtml = processedHtml.replace(regex, `$1${signedUrl}$2`);
			processedComponentsJs = processedComponentsJs.replace(regex, `$1${signedUrl}$2`);
			processedComponentsCss = processedComponentsCss.replace(regex, `$1${signedUrl}$2`);
			processedTheme = processedTheme.replace(regex, `$1${signedUrl}$2`);
			processedStyles = processedStyles.replace(regex, `$1${signedUrl}$2`);
		});
	}

	// Combine gallery HTML with design system CSS and JS into a complete HTML document
	// Components script must be in head and run before body content is parsed
	// so that custom elements are registered before the browser encounters them
	// Resolve the data-mode attribute for the html tag
	const dataModeAttr = activeMode ? ` data-mode="${activeMode}"` : '';

	const resolvedPreviewBg =
		previewBg || config?.display?.bg || `var(--ds-color-bg, ${DS_EDITOR_PREVIEW_BG_DEFAULT})`;

	const fullHtml = `
<!DOCTYPE html>
<html lang="en"${dataModeAttr}>
<head>
	<meta charset="UTF-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<title>Gallery Preview</title>
	${fontsInclude}
	${iconPackInclude}
	${customFontsCss ? `<style>\n${customFontsCss}\n</style>` : ''}
	<style>
		${processedTheme}
	</style>
	<style>
		${processedStyles}
	</style>
	<style>
		${processedComponentsCss}
	</style>
	<style>
		body {
			margin: 0;
			padding: 20px;
			font-family: var(--ds-font-sans, system-ui, sans-serif);
			background: ${resolvedPreviewBg};
			color: var(--ds-color-text, #0f172a);
		}
	</style>
	<script>
		// Shared stylesheet for Shadow DOM components — lets child components inside
		// a shadow root use global DS classes (recipes, component CSS, icon fonts, etc.)
		(function() {
			var sheet = new CSSStyleSheet();
			var css = Array.from(document.querySelectorAll('head > style')).map(function(s) { return s.textContent; }).join('\\n');
			sheet.replaceSync(css);
			window.__DS_STYLES = sheet;
		})();
	</script>
	<script>
		${processedComponentsJs}
	</script>
</head>
<body>
	${processedHtml}
	${iconPackInit}
	<script>
		// Prevent all link clicks from navigating outside the preview iframe
		// srcDoc iframes resolve links relative to parent document
		document.addEventListener('click', function(e) {
			const anchor = e.target.closest('a');
			// we used to only prevent # links, but for now i think it's safest to prevent all links
			// make it like this to prevent only # links: if (anchor && anchor.getAttribute('href')?.startsWith('#')) {
			if (anchor) {
				e.preventDefault();
			}
		}, true);
	</script>
</body>
</html>
	`.trim();

	return (
		<iframe
			ref={iframeRef}
			srcDoc={fullHtml}
			onLoad={onIframeLoad}
			style={{
				width: '100%',
				height: autoHeight ? '100px' : '100%',
				border: 'none',
				background: styledTheme.gray[0],
				pointerEvents: isDragging ? 'none' : 'auto',
			}}
			title="Gallery Preview"
			sandbox="allow-scripts allow-same-origin"
		/>
	);
};

export default GalleryPreview;
